// Admin Dashboard › Leaders Bonus (Alex, 2026-09-28): one warehouse
// scored against its current pay period on four cards —
//   1. On Time Shipment Rate  — period-to-date rate vs the period's target %
//   2. Days below target      — scored due days whose own rate missed it
//   3. Items booked in        — BF + Big scan-sheet units vs daily × working days
//   4. Items put away         — HOT/COLD tote units vs daily × working days
// Targets live under Settings › Leaders Bonus; the server derives the
// period figures (working days = Mon–Fri minus UK bank holidays) and the
// "expected by today" pace line. Same card chrome, chart and colour
// vocabulary as the Team view so the two pages read alike.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarX2, PackageCheck, PackageOpen, Trophy, Truck } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useWarehouseScope } from "../access/useWarehouseScope";
import {
    LeadersBonusDashboard,
    LeadersBonusMetric,
    LeadersBonusPeriod,
    LeadersBonusPoints,
    LeadersBonusScore,
    fetchLeadersBonusDashboard,
    fetchLeadersBonusPeriods,
} from "../api/leadersBonus";
import { BandIcon, DailyChart, onTimePct } from "./DashboardTeam";
import {
    BAND_AMBER, BAND_FLYING, BAND_GREEN, BAND_RED,
    BonusBand, ZONE_EMOJI, ZONE_LABEL_KEY,
    dailyBarColour, failDaysBand, onTimeBandFor, paceBand, progressPct,
} from "./helpers/leadersBonusBands";
import { updatedAgo } from "./helpers/dashboardSnapshot";

// Ring colour by share of the KPI's maximum points: none = red, under 60% =
// amber, the rest green, the top step the Race card's bright green.
const ringColour = (points: number, max: number): string => {
    if (points <= 0) return BAND_RED;
    if (max > 0 && points >= max) return BAND_FLYING;
    if (max > 0 && points / max < 0.6) return BAND_AMBER;
    return BAND_GREEN;
};

// One ring gauge: filled to points ÷ max, the points figure in the middle.
const RING_R = 30;
const RING_C = 2 * Math.PI * RING_R;
const PointsRing = ({ points, max }: { points: number; max: number }) => {
    const frac = max > 0 ? Math.min(1, points / max) : 0;
    const colour = ringColour(points, max);
    return (
        <svg viewBox="0 0 76 76" className="lb-ring" role="img" aria-label={`${points} / ${max}`}>
            <circle className="lb-ring-track" cx="38" cy="38" r={RING_R} />
            <circle
                className="lb-ring-fill"
                cx="38" cy="38" r={RING_R}
                style={{ stroke: colour, strokeDasharray: `${RING_C * frac} ${RING_C}` }}
                transform="rotate(-90 38 38)"
            />
            <text className="lb-ring-num" x="38" y="38">{points}</text>
        </svg>
    );
};

const POLL_MS = 60_000;
const KPI_CHART_PX = 150;

const fmtInt = (n: number) => n.toLocaleString();
const fmt1 = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
};
const fmtDayLong = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

interface Props {
    /** The page's warehouse filter; "" (all) lets this view pick the first warehouse with a period. */
    warehouseFilter: string;
}

const DashboardLeadersBonus = ({ warehouseFilter: pageWarehouseFilter }: Props) => {
    const { t } = useTranslation();
    // Without cross_warehouse the view is pinned to the person's own warehouse.
    const warehouseScope = useWarehouseScope();
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pageWarehouseFilter;
    const [warehouse, setWarehouse] = useState<string | null>(warehouseFilter || null);
    const [autoTried, setAutoTried] = useState(false);
    const [periodId, setPeriodId] = useState<number | null>(null);
    const [data, setData] = useState<LeadersBonusDashboard | null>(null);
    const [loading, setLoading] = useState(true);
    const [nowMs, setNowMs] = useState(() => Date.now());

    // Warehouse: the page filter when set; otherwise the first warehouse
    // that has a period covering today (else any period), so a superAdmin
    // landing on "All warehouses" still sees something.
    useEffect(() => {
        if (warehouseFilter) {
            setWarehouse(warehouseFilter);
            setPeriodId(null);
            setAutoTried(true);
            return;
        }
        let cancelled = false;
        setWarehouse(null);
        setPeriodId(null);
        setAutoTried(false);
        (async () => {
            try {
                const fetched = await fetchLeadersBonusPeriods();
                const today = fetched.today;
                const allowed = new Set(warehouseScope.limit(fetched.periods.map((p) => p.warehouse)));
                const periods = fetched.periods.filter((p) => allowed.has(p.warehouse));
                if (cancelled) return;
                const current = periods.find((p) => p.starts_on <= today && p.ends_on >= today);
                const pick = current ?? periods[0] ?? null;
                setWarehouse(pick ? pick.warehouse : null);
            } catch {
                if (!cancelled) toast.error(t("server_error"), { toastId: "leaders-bonus-dash-err" });
            } finally {
                if (!cancelled) setAutoTried(true);
            }
        })();
        return () => { cancelled = true; };
    }, [warehouseFilter, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        if (!warehouse) return;
        try {
            const payload = await fetchLeadersBonusDashboard(warehouse, periodId);
            setData(payload);
            setNowMs(Date.now());
        } catch {
            toast.error(t("server_error"), { toastId: "leaders-bonus-dash-err" });
        } finally {
            setLoading(false);
        }
    }, [warehouse, periodId, t]);

    useEffect(() => {
        if (!warehouse) { setLoading(false); return undefined; }
        setLoading(true);
        load();
        const id = window.setInterval(() => {
            if (document.visibilityState === "visible") load();
        }, POLL_MS);
        return () => window.clearInterval(id);
    }, [warehouse, load]);

    useEffect(() => {
        const id = window.setInterval(() => setNowMs(Date.now()), 5_000);
        return () => window.clearInterval(id);
    }, []);

    const periods: LeadersBonusPeriod[] = data?.periods ?? [];
    const period = data?.period ?? null;
    const today = data?.today ?? "";

    const updatedLabel = useMemo(() => {
        const ago = updatedAgo(data?.snapshot_at, nowMs);
        if (!ago) return null;
        return "secs" in ago
            ? t("dashboard_updated_secs", { n: String(ago.secs) })
            : t("dashboard_updated_mins", { n: String(ago.mins) });
    }, [data?.snapshot_at, nowMs, t]);

    if (!warehouse) {
        if (!autoTried) return <div className="admin-settings-loading">{t("loading")}</div>;
        return (
            <div className="tdash">
                <div className="lb-dash-empty">
                    <p>{t("dashboard_lb_select_warehouse")}</p>
                    <Link to="/admin/settings#leaders-bonus" className="btn btn-ghost btn-sm">{t("leaders_bonus_settings")}</Link>
                </div>
            </div>
        );
    }
    if (loading && !data) return <div className="admin-settings-loading">{t("loading")}</div>;

    const header = (
        <div className="lb-dash-head">
            <div className="lb-dash-head-main">
                <h2 className="tdash-section-heading lb-dash-title">{t("dashboard_scope_leaders_bonus")} · {warehouse}</h2>
                {period && (
                    <span className="lb-dash-meta">
                        {t("dashboard_lb_period_meta", {
                            from: fmtDate(period.starts_on),
                            to: fmtDate(period.ends_on),
                            days: String(period.working_days),
                            elapsed: String(period.working_days_elapsed),
                        })}
                    </span>
                )}
            </div>
            <div className="lb-dash-head-right">
                {updatedLabel && <span className="dash-updated" aria-live="polite">{updatedLabel}</span>}
                {periods.length > 0 && (
                    <select
                        className="dash-pill-select"
                        value={period?.id ?? ""}
                        onChange={(e) => setPeriodId(e.target.value ? Number(e.target.value) : null)}
                        aria-label={t("dashboard_lb_period_label")}
                    >
                        {periods.map((p) => (
                            <option key={p.id} value={p.id}>
                                {fmtDate(p.starts_on)} – {fmtDate(p.ends_on)}
                                {p.starts_on <= today && p.ends_on >= today ? ` · ${t("leaders_bonus_current")}` : ""}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        </div>
    );

    if (!period || !data?.on_time || !data.booked_in || !data.put_away) {
        return (
            <div className="tdash">
                {header}
                <div className="lb-dash-empty">
                    <p>{t("dashboard_lb_no_periods", { warehouse })}</p>
                    <Link to="/admin/settings#leaders-bonus" className="btn btn-primary btn-sm">{t("leaders_bonus_settings")}</Link>
                </div>
            </div>
        );
    }

    const ot = data.on_time;
    const otPct = ot.rate_pct;
    const otBand = otPct !== null ? onTimeBandFor(otPct, ot.target_pct) : null;
    const fails = ot.fail_days;
    const failBand = failDaysBand(fails.length);
    const chartToday = today || new Date().toISOString().slice(0, 10);

    // The zone pill under each card title: Flying 🔥 / On track 😎 / Behind 😮 /
    // Danger zone ⚠️ — the Race card's vocabulary, scored against this period.
    const zonePill = (band: BonusBand) => (
        <span className={`lb-zone lb-zone--${band.zone}`} style={{ color: band.colour }}>
            <span aria-hidden="true">{ZONE_EMOJI[band.zone]}</span>
            {t(ZONE_LABEL_KEY[band.zone])}
        </span>
    );

    // Bonus points card: headline total out of the ladders' maximum, then a
    // ring per KPI with its value, points and the next step to reach.
    const METRIC_TITLE_KEY: Record<LeadersBonusMetric, string> = {
        on_time: "dashboard_team_on_time_rate",
        booked_in: "dashboard_lb_booked_in",
        put_away: "dashboard_lb_put_away",
    };
    const fmtStep = (metric: LeadersBonusMetric, v: number) =>
        metric === "on_time" ? `${fmt1(v)}%` : t("dashboard_lb_pct_of_target_value", { pct: fmt1(v) });
    const pointsRing = (metric: LeadersBonusMetric, score: LeadersBonusScore) => (
        <div key={metric} className="lb-ring-item">
            <PointsRing points={score.points} max={score.max} />
            <div className="lb-ring-meta">
                <span className="lb-ring-name">{t(METRIC_TITLE_KEY[metric])}</span>
                <span className="lb-ring-value">
                    {score.value === null
                        ? t("dashboard_lb_no_value")
                        : metric === "on_time"
                            ? `${fmt1(score.value)}%`
                            // Items are scored on pace: units so far vs units expected by now.
                            : t("dashboard_lb_pace_value", { pct: fmt1(score.value) })}
                </span>
                <span className="lb-ring-pts" style={{ color: ringColour(score.points, score.max) }}>
                    {t("dashboard_lb_pts", { n: String(score.points) })} · {t("dashboard_lb_points_of", { max: String(score.max) })}
                </span>
                <span className="lb-ring-next">
                    {score.next
                        ? t("dashboard_lb_next_step", { from: fmtStep(metric, score.next.from), points: String(score.next.points) })
                        : t("dashboard_lb_max_reached")}
                </span>
            </div>
        </div>
    );
    const pointsCard = (pts: LeadersBonusPoints) => (
        <section className="tdash-card tdash-card--kpi lb-card lb-card--points">
            <Trophy className="tdash-kpi-icon" size={88} strokeWidth={1.4} aria-hidden="true" />
            <h3 className="tdash-card-title">{t("dashboard_lb_points")}</h3>
            <div className="tdash-headline-block">
                <div className="tdash-headline">
                    <span className="tdash-headline-num" style={{ color: ringColour(pts.total, pts.max) }}>{pts.total}</span>
                    <span className="tdash-headline-unit">{t("dashboard_lb_points_of", { max: String(pts.max) })}</span>
                </div>
            </div>
            <div className="lb-rings">
                {pointsRing("on_time", pts.on_time)}
                {pointsRing("booked_in", pts.booked_in)}
                {pointsRing("put_away", pts.put_away)}
            </div>
        </section>
    );

    // One "units toward the period target" card — booked in and put away
    // share everything but their icon, data and hint.
    const unitsCard = (
        key: "booked_in" | "put_away",
        Icon: typeof PackageOpen,
        titleKey: string,
        hintKey: string,
        block: { units: number; daily_target: number; target: number; expected_to_date: number;
                 daily: Array<{ date: string; units: number; working: boolean }> },
    ) => {
        // Pace (units vs expected by now) drives the zone pill and colours
        // only — the numbers themselves stay off the card (Alex, 2026-09-28).
        const band = paceBand(block.units, block.expected_to_date, block.target);
        const pct = progressPct(block.units, block.target);
        const remaining = Math.max(0, block.target - block.units);
        const daysLeft = period.working_days_remaining ?? Math.max(0, period.working_days - period.working_days_elapsed);
        const needPerDay = daysLeft > 0 ? Math.ceil(remaining / daysLeft) : null;
        const maxUnits = Math.max(block.daily_target, ...block.daily.map((d) => d.units));
        const scaleMax = Math.max(1, Math.ceil(maxUnits * 1.08));
        return (
            <section className={`tdash-card tdash-card--kpi lb-card lb-card--${key}`}>
                <Icon className="tdash-kpi-icon" size={88} strokeWidth={1.4} aria-hidden="true" />
                <h3 className="tdash-card-title">{t(titleKey)}</h3>
                <div className="tdash-headline-block">
                    {zonePill(band)}
                    <div className="tdash-headline">
                        <span className="tdash-headline-num" style={{ color: band.colour }}>{fmtInt(block.units)}</span>
                        <span className="tdash-headline-unit">{t("dashboard_lb_of_target", { target: fmtInt(block.target) })}</span>
                        <BandIcon band={band} />
                    </div>
                    <div className="tdash-progress-row lb-progress">
                        <div className="tdash-progress-track">
                            <div
                                className="tdash-progress-fill"
                                style={{ width: `${pct}%`, backgroundColor: band.colour }}
                            />
                        </div>
                        <div className="tdash-progress-meta">
                            <span className="tdash-progress-name">{t("dashboard_lb_progress_pct", { pct: String(pct) })}</span>
                            <span className="tdash-progress-pct">{t("dashboard_lb_daily_avg", { n: fmtInt(block.daily_target) })}</span>
                        </div>
                    </div>
                    {/* What the remaining working days (today included) must average to reach 100%. */}
                    <div className={`tdash-delta ${needPerDay !== null && needPerDay > block.daily_target ? "tdash-delta--down" : "tdash-delta--none"}`}>
                        {remaining <= 0
                            ? t("dashboard_lb_target_reached")
                            : needPerDay === null
                                ? t("dashboard_lb_no_days_left")
                                : t("dashboard_lb_need_per_day", { n: fmtInt(needPerDay) })}
                    </div>
                    <div className="tdash-caption">{t(hintKey)}</div>
                </div>
                <DailyChart
                    today={chartToday}
                    colour={(v) => dailyBarColour(v, block.daily_target)}
                    heightPx={KPI_CHART_PX}
                    scaleMax={scaleMax}
                    target={block.daily_target > 0
                        ? { value: block.daily_target, label: t("dashboard_lb_daily_target", { n: fmtInt(block.daily_target) }) }
                        : undefined}
                    points={block.daily.map((d) => ({
                        date: d.date,
                        value: d.units > 0 ? d.units : null,
                        // Weekend / bank-holiday units count toward the total but
                        // aren't judged against the daily target — grey, not red.
                        muted: !d.working,
                        tip: d.working
                            ? t("dashboard_lb_tip_units", { n: fmtInt(d.units) })
                            : `${t("dashboard_lb_tip_units", { n: fmtInt(d.units) })} · ${t("dashboard_lb_non_working")}`,
                    }))}
                />
            </section>
        );
    };

    return (
        <div className="tdash">
            {header}
            <div className="tdash-kpi-grid lb-grid">
                {/* 0 — Bonus points across the three KPIs (full width) */}
                {data.points && pointsCard(data.points)}

                {/* 1 — On Time Shipment Rate vs the period's target */}
                <section className="tdash-card tdash-card--kpi lb-card lb-card--on-time">
                    <Truck className="tdash-kpi-icon" size={88} strokeWidth={1.4} aria-hidden="true" />
                    <h3 className="tdash-card-title">{t("dashboard_team_on_time_rate")}</h3>
                    <div className="tdash-headline-block">
                        {otBand && zonePill(otBand)}
                        <div className={`tdash-headline${otPct === null ? " tdash-headline--quiet" : ""}`}>
                            <span className="tdash-headline-num" style={otBand ? { color: otBand.colour } : undefined}>
                                {otPct !== null ? fmt1(otPct) : "—"}
                            </span>
                            <span className="tdash-headline-unit">%</span>
                            {otBand && <BandIcon band={otBand} />}
                        </div>
                        <div className="tdash-delta tdash-delta--none">
                            {ot.total === 0
                                ? t("dashboard_team_no_orders_in_range")
                                : t("dashboard_team_on_time_count", { on_time: fmtInt(ot.on_time), total: fmtInt(ot.total) })}
                        </div>
                        <div className="tdash-caption">
                            {t("dashboard_team_target", { pct: String(ot.target_pct) })}
                            {data.dispatch_cutoff ? ` · ${t("dashboard_team_dispatch_cutoff", { time: data.dispatch_cutoff })}` : ""}
                        </div>
                    </div>
                    <DailyChart
                        today={chartToday}
                        colour={(v) => onTimeBandFor(v, ot.target_pct).colour}
                        heightPx={KPI_CHART_PX}
                        scaleMax={100}
                        target={{ value: ot.target_pct, label: t("dashboard_team_target", { pct: String(ot.target_pct) }) }}
                        points={ot.daily.map((d) => {
                            const pct = onTimePct(d.on_time, d.total);
                            return {
                                date: d.date,
                                value: pct,
                                tip: pct !== null
                                    ? t("dashboard_team_tip_on_time", { shipped: fmtInt(d.on_time), due: fmtInt(d.total), pct: fmt1(pct) })
                                        + (d.early > 0 ? ` · ${t("dashboard_team_tip_early", { early: fmtInt(d.early) })}` : "")
                                    : "—",
                            };
                        })}
                    />
                </section>

                {/* 2 — Days below target */}
                <section className="tdash-card tdash-card--kpi lb-card lb-card--fail-days">
                    <CalendarX2 className="tdash-kpi-icon" size={88} strokeWidth={1.4} aria-hidden="true" />
                    <h3 className="tdash-card-title">{t("dashboard_lb_fail_days")}</h3>
                    <div className="tdash-headline-block">
                        <div className="tdash-headline">
                            <span className="tdash-headline-num" style={{ color: failBand.colour }}>{fails.length}</span>
                            <span className="tdash-headline-unit">{t("dashboard_lb_fail_days_unit")}</span>
                            <BandIcon band={failBand} />
                        </div>
                        <div className="tdash-delta tdash-delta--none">
                            {t("dashboard_lb_fail_days_of", { n: String(ot.scored_days), pct: String(ot.target_pct) })}
                        </div>
                    </div>
                    {fails.length === 0 ? (
                        <p className="lb-fail-none">
                            {ot.scored_days > 0 ? t("dashboard_lb_no_fail_days") : t("dashboard_lb_no_scored_days")}
                        </p>
                    ) : (
                        <ul className="lb-fail-list">
                            {fails.map((d) => (
                                <li key={d.date} className="lb-fail-item">
                                    <span className="lb-fail-date">{fmtDayLong(d.date)}</span>
                                    <span className="lb-fail-pct" style={{ color: onTimeBandFor(d.pct, ot.target_pct).colour }}>
                                        {fmt1(d.pct)}%
                                    </span>
                                    <span className="lb-fail-detail">
                                        {t("dashboard_lb_fail_day_line", { late: fmtInt(d.late), due: fmtInt(d.total) })}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* 3 — Items booked in */}
                {unitsCard("booked_in", PackageOpen, "dashboard_lb_booked_in", "dashboard_lb_booked_in_hint", data.booked_in)}

                {/* 4 — Items put away */}
                {unitsCard("put_away", PackageCheck, "dashboard_lb_put_away", "dashboard_lb_put_away_hint", data.put_away)}
            </div>
        </div>
    );
};

export default DashboardLeadersBonus;
